import { useState, useEffect, useRef, CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { colors } from "@styles/colors";
import {
  PRACTICE_SCENARIOS,
  PracticeScenario,
  usePracticeStore,
} from "@utils/practice/practiceMode";
import { useOrder } from "@utils/store/cart/useOrder";
import { startScenario } from "./practiceFlow";
import { getStepKey, STEP_GUIDES } from "./guides";

/**
 * 客服練習模式的「跟著做」教練面板（全域浮層，僅練習模式且非 /practice 首頁顯示）。
 * 針對不熟手機的客服：底部一次一句大字指令（告訴他要按哪個鈕）+ 做完自動前進到下一步 + 我卡住了。
 * 不加聚光燈/變暗/手指，避免蓋住或干擾畫面。用 portal 掛 body，避免被 overflow/sheet 蓋住。
 */
const PANEL_Z = 1000050;
const AMBER = "#b45309";

/**
 * 「離開腳本」的頁面（多為帳號類頁）中性說明：不叫客戶做特定動作，只說明這頁能做什麼，
 * 讓晃進來的客服知道這頁的用途，而不是被要求去按與當前情境無關的按鈕。
 */
const OFF_SCRIPT_DESC: Record<string, string> = {
  accountHome: "這是「帳號」頁：可以查看與切換你的瓦斯帳戶，或進到下方的地址、付款方式、優惠券、發票等設定。",
  accountManage: "這是「帳號」頁：可以查看與切換帳戶，並管理地址、付款方式、優惠券、發票等設定。",
  accountDetail: "這是帳戶詳情：可以看這個帳戶的成員、邀請成員，或編輯帳戶資料。",
  accountPayment: "這是付款方式頁：可以新增或設定常用的付款方式。",
  accountCoupons: "這是優惠券頁：可以查看目前可用的優惠券。",
};

/**
 * 量測底部分頁列（PageWrapper 的 Tabs.List）實際高度，讓面板剛好貼齊其上緣。
 * - 在 #root 內找「同時含首頁/訂單/帳號」且面積最小的容器＝分頁列（面板本身 portal 到 body，不在 #root 內，不會誤判）。
 * - 找不到（註冊/詳情等無分頁列頁面）→ 回 0，面板貼底。
 */
const measureBarOffset = (): number => {
  const root = document.getElementById("root");
  if (!root) return 0;
  let best: HTMLElement | null = null;
  let bestArea = Infinity;
  root.querySelectorAll<HTMLElement>("div").forEach((el) => {
    const t = el.textContent || "";
    if (!(t.includes("首頁") && t.includes("訂單") && t.includes("帳號"))) return;
    const r = el.getBoundingClientRect();
    const area = r.width * r.height;
    if (area > 0 && area < bestArea) {
      best = el;
      bestArea = area;
    }
  });
  if (!best) return 0;
  const rect = (best as HTMLElement).getBoundingClientRect();
  return Math.max(0, Math.round(window.innerHeight - rect.top));
};

/**
 * 各步驟要圈出的目標。
 * - mode "card"（預設）：圈最外層可點容器（整張卡片/按鈕）。
 * - mode "content"：只圈可點容器內的內容群（icon+label），用於整欄寬的底部分頁，避免框太寬。
 */
interface SpotlightTarget {
  text: string;
  mode: "card" | "content";
}
const spotlightTarget = (stepKey: string, scenario: PracticeScenario): SpotlightTarget | null => {
  switch (stepKey) {
    case "home":
      // 下單→圈「瓦斯訂購」；首次設定→圈「完成加入設定」卡；家庭→圈「帳號」分頁；其餘不圈
      if (scenario === "orderGasShop") return { text: "瓦斯訂購", mode: "content" };
      if (scenario === "firstJoinSetup") return { text: "完成加入設定", mode: "card" };
      if (scenario === "joinFamily" || scenario === "getInvite") return { text: "帳號", mode: "content" };
      return null;
    case "accountHome":
      // 只有家庭情境才在帳號頁指定按鈕：加入別人帳戶→「新增」、自己當邀請人→帳戶卡片。
      // 其餘情境（掃碼註冊、下單、帳號管理等）到帳號頁不指定，讓客服自由操作。
      if (scenario === "joinFamily") return { text: "新增", mode: "card" };
      if (scenario === "getInvite") return { text: "練習家庭帳戶", mode: "card" };
      return null;
    case "accountDetail":
      return { text: "邀請成員", mode: "card" };
    case "familyJoin":
      // 指引以 guide.action 為主（涵蓋選項→輸入碼→加入整段）；此處僅備援指到選項本身。
      return { text: "加入既有的瓦斯帳戶", mode: "card" };
    case "getInvite":
      return { text: "產生邀請碼", mode: "card" };
    // 下單流程（按鈕文字固定的步驟才圈；金額/動態文字的按鈕靠面板文字提示）
    case "orderProducts":
      return { text: "結帳", mode: "card" };
    case "orderInstall":
      return { text: "加入購物車", mode: "card" };
    // orderTime 為適應式（依配送選擇狀態移動），於外層另行計算。
    case "orderCart":
      return { text: "確認下單", mode: "card" };
    case "lineNotice":
      // 不指定目標：指定會讓面板抬到按鈕上緣，正好蓋住這頁的主角（那張通知卡）。
      // 面板貼底時卡片與兩顆按鈕都在面板上方，看得完整；要按哪顆由 guide.action 說明。
      return null;
    case "kioskHome":
      // 首頁不硬指單一鈕：有「重新下訂上次」可重訂、沒有就選商品，皆由 kioskHome guide.action 說明。
      return null;
    case "kioskCart":
      return { text: "前往結帳", mode: "card" };
    case "kioskConfirm":
      return { text: "確認送出", mode: "card" };
    default:
      return null;
  }
};

/** 在畫面上找出文字完全相符、面積最小的元素（最貼近的可點目標）。 */
const findByText = (text: string): HTMLElement | null => {
  const els = Array.from(document.querySelectorAll<HTMLElement>("a,button,span,div,p"));
  let best: HTMLElement | null = null;
  let bestArea = Infinity;
  for (const el of els) {
    if ((el.textContent || "").trim() !== text) continue;
    const r = el.getBoundingClientRect();
    const area = r.width * r.height;
    if (area > 0 && area < bestArea) {
      best = el;
      bestArea = area;
    }
  }
  return best;
};

/** 配送時間步驟的適應式目標：沒選日期→指隔天日期；選了日期沒選時段→指第一個時段；立即配送→不指。 */
const orderTimeTarget = (deliveryTime: { type: string; date: string | null; slotId: number | null }): SpotlightTarget | null => {
  if (deliveryTime.type === "immediate") return null; // 立即配送已就緒，按下一步即可
  if (!deliveryTime.date) {
    const d = new Date();
    d.setDate(d.getDate() + 1); // 假資料第一個可選日為「明天」
    return { text: String(d.getDate()), mode: "content" };
  }
  if (!deliveryTime.slotId) return { text: "09:00 - 12:00", mode: "card" }; // 第一個時段
  return null;
};

const CoachPanel = () => {
  const active = usePracticeStore((s) => s.active);
  const scenario = usePracticeStore((s) => s.scenario);
  const location = useLocation();
  const navigate = useNavigate();
  const deliveryTime = useOrder((s) => s.deliveryTime);
  const [barOffset, setBarOffset] = useState(0);
  const [help, setHelp] = useState(false);
  const [collapsed, setCollapsed] = useState(false); // 收合成小膠囊，讓客服能操作被面板蓋住的畫面
  const userCollapsedRef = useRef(false); // 使用者手動「收起」後保持收合，步驟切換不再自動彈開（展開才恢復）
  const [targetTop, setTargetTop] = useState<number | null>(null); // 目標按鈕落在下半部時記其上緣 → 面板浮到它上方，不蓋住它
  const [installStep, setInstallStep] = useState<string | null>(null); // kiosk 安裝精靈當前步驟（sheet 內部狀態，需輪詢才能讓面板跟著換提示）
  const [firstJoinDone, setFirstJoinDone] = useState(false); // 首次設定完成後首頁出現「立即預約叫桶」→ 教學改指向訂購按鈕

  const onPractice = location.pathname === "/practice";
  const isDone = new URLSearchParams(location.search).get("done") != null;
  const stepKey = getStepKey(location.pathname, isDone);
  const on = active && !onPractice;

  // 底部分頁列高度量測，讓面板貼齊其上緣。
  useEffect(() => {
    if (!on) return;
    const update = () => setBarOffset(measureBarOffset());
    update();
    const id = window.setInterval(update, 400);
    window.addEventListener("resize", update);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", update);
    };
  }, [on, location.pathname]);

  // 步驟一變（多半是完成上一步導頁）→ 重新展開、收起說明/示範。
  useEffect(() => {
    setHelp(false);
    // 掃碼註冊是自成一頁的引導、底部有樣本按鈕，面板預設收合成小膠囊避免蓋住；其餘步驟展開。
    // 但使用者手動「收起」後（userCollapsedRef）步驟切換不再自動彈開，尊重其選擇。
    if (!userCollapsedRef.current) setCollapsed(stepKey === "onbQr");
  }, [stepKey, on]);

  // 首次加入設定情境：完成設定回到首頁後，橘色「完成加入設定」卡消失、出現「立即預約叫桶」，
  // 教學需從「點設定卡」切換成「點訂購按鈕」。以輪詢偵測按鈕是否已出現（同一路由不重繪）。
  useEffect(() => {
    if (!on || stepKey !== "home" || scenario !== "firstJoinSetup") {
      setFirstJoinDone(false);
      return;
    }
    const check = () => setFirstJoinDone(!!findByText("立即預約叫桶"));
    check();
    const id = window.setInterval(check, 300);
    return () => window.clearInterval(id);
  }, [on, stepKey, scenario]);

  // 目標按鈕（只用於面板文字提示要按哪個鈕，不再畫聚光燈）。
  const target: SpotlightTarget | null = firstJoinDone
    ? { text: "立即預約叫桶", mode: "card" }
    : on ? (stepKey === "orderTime" ? orderTimeTarget(deliveryTime) : spotlightTarget(stepKey, scenario)) : null;

  // 特例：kiosk 點一款瓦斯後彈出的「安裝精靈」sheet（overlay，URL 仍是 /kiosk）→ 依當前步驟給指引。
  // sheet 的步驟是它自己的 state，CoachPanel 不會因此重繪，故用下方輪詢寫進 installStep 觸發重繪。
  const INSTALL_GUIDES: Record<string, { msg: string; btn: string }> = {
    use: { msg: "選這桶瓦斯要用在哪裡（例如瓦斯爐），選好按「下一步」。", btn: "下一步" },
    lift: { msg: "選有沒有電梯，再按「下一步」。", btn: "下一步" },
    floor: { msg: "選在幾樓，再按「下一步」。", btn: "下一步" },
    qty: { msg: "選好數量，再按「加入購物車」。", btn: "加入購物車" },
  };
  const installGuide = installStep ? INSTALL_GUIDES[installStep] : null;

  useEffect(() => {
    if (!on || stepKey !== "kioskHome") {
      setInstallStep(null);
      return;
    }
    const check = () => {
      const s = findByText("安裝在哪裡？")
        ? "use"
        : findByText("有電梯嗎？")
          ? "lift"
          : findByText("在幾樓？")
            ? "floor"
            : findByText("加入購物車")
              ? "qty"
              : null;
      setInstallStep((prev) => (prev === s ? prev : s));
    };
    check();
    const id = window.setInterval(check, 250);
    return () => window.clearInterval(id);
  }, [on, stepKey]);

  // 追蹤目標按鈕位置：落在畫面下半部（如底部「結帳」CTA，會被底部面板蓋住）→ 記其上緣，
  // 讓面板浮到它「上方」而不是蓋住它；在上半部則維持面板貼底。
  const targetText = installGuide ? installGuide.btn : target?.text ?? null;
  useEffect(() => {
    if (!on || !targetText) {
      setTargetTop(null);
      return;
    }
    const check = () => {
      const el = findByText(targetText);
      if (el) {
        const r = el.getBoundingClientRect();
        setTargetTop(r.top + r.height / 2 > window.innerHeight * 0.5 ? r.top : null);
      } else {
        setTargetTop(null);
      }
    };
    check();
    const id = window.setInterval(check, 300);
    return () => window.clearInterval(id);
  }, [on, targetText]);

  if (!on) return null;

  // 首頁/帳號頁依情境改用對應教學（同一路由不同情境給不同腳本）。
  const guideKey =
    stepKey === "home" && scenario === "orderGasShop" ? "homeOrder"
    : stepKey === "home" && scenario === "firstJoinSetup" ? "firstJoinHome"
    : stepKey === "home" && scenario === "notifyExpiring" ? "noticeHome" // 從通知卡按「查看使用天數」過來
    : stepKey === "accountHome" && scenario === "manageAccount" ? "accountManage"
    // 掃碼到 kiosk：無上次訂購，改教選商品。到期通知走的是既有客戶（有上次訂購），沿用 kioskHome。
    : stepKey === "kioskHome" && scenario !== "orderKiosk" && scenario !== "notifyExpiring" ? "kioskHomeNew"
    : stepKey;
  const scenarioMeta = PRACTICE_SCENARIOS.find((s) => s.key === scenario);

  // 帳號頁只屬於家庭/帳號類情境；其他情境（掃碼註冊、下單等）晃到帳號頁＝離開了練習腳本，
  // 不套用家庭帳號教學（避免叫他按與情境無關的「新增」），改中性說明「這頁能做什麼」。
  const isAccountScenario = scenario === "joinFamily" || scenario === "getInvite" || scenario === "manageAccount";
  const offScript = stepKey in OFF_SCRIPT_DESC && !isAccountScenario;

  const guide = offScript ? null : guideKey in STEP_GUIDES ? STEP_GUIDES[guideKey as keyof typeof STEP_GUIDES] : null;

  const instruction = firstJoinDone
    ? "設定完成！按亮起來的「立即預約叫桶」就能開始訂購。"
    : installGuide
    ? installGuide.msg
    : offScript
    ? OFF_SCRIPT_DESC[stepKey]
    : guide?.action ||
      (target ? `請按畫面上的「${target.text}」` : null) ||
      guide?.customerSees ||
      "跟著畫面操作即可。";

  const btn = (label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        border: `1.5px solid ${colors.border || "#e2e8f0"}`,
        background: "#fff",
        color: colors.grayDark || "#334155",
        borderRadius: 12,
        padding: "12px 8px",
        fontSize: 14.5,
        fontWeight: 800,
        cursor: "pointer",
      }}>
      {label}
    </button>
  );

  // 收合：只留右下小膠囊，讓客服能點到被面板蓋住的畫面（如掃碼頁底部樣本）。
  if (collapsed) {
    return createPortal(
      <button
        type="button"
        onClick={() => { userCollapsedRef.current = false; setCollapsed(false); }}
        style={{
          position: "fixed", right: 12, bottom: barOffset + 12, zIndex: PANEL_Z,
          border: "none", borderRadius: 999, padding: "10px 16px", background: AMBER, color: "#fff",
          fontWeight: 800, fontSize: 13, boxShadow: "0 4px 14px rgba(0,0,0,0.28)", cursor: "pointer",
        }}>
        🎓 看提示 ▴
      </button>,
      document.body,
    );
  }

  // 目標在下半部 → 面板浮到它上方（bottom 抬高到目標上緣，露出底部 CTA）；否則貼分頁列上緣。
  const liftBottom = targetTop != null ? Math.max(barOffset, Math.round(window.innerHeight - targetTop + 8)) : barOffset;
  const containerPos: CSSProperties = { bottom: liftBottom, padding: "0 10px 10px" };

  return createPortal(
        <div style={{ position: "fixed", left: 0, right: 0, zIndex: PANEL_Z, margin: "0 auto", maxWidth: 480, ...containerPos }}>
          <div style={{ background: "#fff", borderRadius: 18, boxShadow: "0 -6px 28px rgba(0,0,0,0.22)", border: `2px solid ${AMBER}`, overflow: "hidden" }}>
            {/* 頂列（所有路徑一律回 /practice 選單；真正離開練習在選單頁） */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", background: AMBER, color: "#fff" }}>
              <span style={{ fontWeight: 800, fontSize: 13 }}>🎓 練習模式 · {scenarioMeta?.label || ""}</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => { userCollapsedRef.current = true; setCollapsed(true); }} style={{ border: "none", background: "rgba(255,255,255,0.22)", color: "#fff", borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>收起 ▾</button>
                <button type="button" onClick={() => navigate("/practice")} style={{ border: "none", background: "#fff", color: AMBER, borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>回選單</button>
              </div>
            </div>

            {/* 主體 */}
            <div style={{ padding: "14px 16px 16px" }}>
                  {/* 一句大字指令 */}
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 26, lineHeight: 1.1 }}>{offScript ? "🧭" : "👉"}</span>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#1f2937", lineHeight: 1.45 }}>{instruction}</div>
                  </div>
                  <div style={{ fontSize: 12.5, color: colors.gray || "#64748b", marginTop: 6, marginLeft: 36 }}>
                    {offScript ? "這一頁不會計入這個練習的進度。" : "做好會自動到下一步。"}
                  </div>

                  {/* 卡住時的更多說明 */}
                  {help && guide && (
                    <div style={{ marginTop: 12, background: "#F8FAFC", borderRadius: 12, padding: "10px 12px" }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: colors.primary }}>這頁在做什麼</div>
                      <div style={{ fontSize: 14, color: "#334155", lineHeight: 1.5, marginTop: 2 }}>{guide.customerSees}</div>
                      {guide.sayThis.length > 0 && (
                        <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                          {guide.sayThis.map((t, i) => (
                            <li key={i} style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.5, marginBottom: 4 }}>{t}</li>
                          ))}
                        </ul>
                      )}
                      {guide.pitfalls.map((t, i) => (
                        <div key={i} style={{ fontSize: 12.5, color: AMBER, marginTop: 6 }}>⚠️ {t}</div>
                      ))}
                    </div>
                  )}

                  {/* 動作按鈕 */}
                  <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                    {!offScript && btn(help ? "收起說明" : "我卡住了", () => setHelp((v) => !v))}
                    {offScript && btn("回到上一步", () => navigate(-1))}
                  </div>
                  <button
                    type="button"
                    onClick={() => startScenario(scenario, navigate)}
                    style={{ width: "100%", marginTop: 8, border: "none", background: "transparent", color: colors.gray || "#64748b", fontSize: 12.5, cursor: "pointer", textDecoration: "underline" }}>
                    做錯了？從頭再練一次
                  </button>
            </div>
          </div>
        </div>,
    document.body,
  );
};

export default CoachPanel;
